import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Users, Plus, Loader2, Mail, Phone, ChevronRight, ClipboardList, UserCheck, UserX, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminTrainersPage = () => {
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newTrainer, setNewTrainer] = useState({ name: '', email: '', password: '', phone: '', specialty: '' });

  useEffect(() => { fetchTrainers(); }, []);

  const fetchTrainers = async () => {
    try {
      const res = await axios.get(`${API}/admin/trainers`);
      setTrainers(res.data);
    } catch (e) {
      toast.error('Error al cargar entrenadores');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newTrainer.name || !newTrainer.email || !newTrainer.password) {
      toast.error('Nombre, email y contrase\u00f1a son obligatorios');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/trainers`, newTrainer);
      toast.success('Entrenador creado');
      setShowCreate(false);
      setNewTrainer({ name: '', email: '', password: '', phone: '', specialty: '' });
      fetchTrainers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear entrenador');
    } finally {
      setCreating(false);
    }
  };

  const toggleStatus = async (trainerId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    try {
      await axios.put(`${API}/admin/trainers/${trainerId}/status`, { status: newStatus });
      toast.success(`Estado actualizado a ${newStatus}`);
      fetchTrainers();
    } catch (e) {
      toast.error('Error al actualizar estado');
    }
  };

  const viewDetail = async (trainerId) => {
    try {
      const res = await axios.get(`${API}/admin/trainers/${trainerId}`);
      setShowDetail(res.data);
    } catch (e) {
      toast.error('Error al cargar detalle');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-trainers">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Entrenadores</h1>
            <p className="text-muted-foreground">{trainers.length} entrenadores registrados</p>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="create-trainer-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Entrenador
          </Button>
        </div>

        {trainers.length === 0 ? (
          <Card className="bg-card border-border border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">No hay entrenadores a{'\u00fa'}n</p>
              <Button onClick={() => setShowCreate(true)}>
                <Plus className="h-4 w-4 mr-2" /> Crear primer entrenador
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {trainers.map((trainer) => (
              <Card key={trainer.id} className="bg-card border-border">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                      <span className="text-lg font-bold text-blue-400">
                        {trainer.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-foreground">{trainer.name}</p>
                        <Badge variant={trainer.status === 'active' ? 'default' : 'destructive'} className="text-xs">
                          {trainer.status === 'active' ? 'Activo' : 'Suspendido'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                        <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{trainer.email}</span>
                        {trainer.specialty && <span>{trainer.specialty}</span>}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                        <span className="flex items-center gap-1"><Users className="h-3 w-3" />{trainer.client_count || 0} clientes</span>
                        <span className="flex items-center gap-1"><ClipboardList className="h-3 w-3" />{trainer.plan_count || 0} planes</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => viewDetail(trainer.id)} data-testid={`view-trainer-${trainer.id}`}>
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleStatus(trainer.id, trainer.status)}
                      className={trainer.status === 'active' ? 'text-red-400 hover:text-red-300' : 'text-green-400 hover:text-green-300'}
                    >
                      {trainer.status === 'active' ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Trainer Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>Nuevo Entrenador</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nombre *</Label>
                <Input value={newTrainer.name} onChange={(e) => setNewTrainer(p => ({...p, name: e.target.value}))} placeholder="Nombre completo" className="bg-zinc-900/50 border-zinc-800" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={newTrainer.email} onChange={(e) => setNewTrainer(p => ({...p, email: e.target.value}))} placeholder="correo@ejemplo.com" className="bg-zinc-900/50 border-zinc-800" />
              </div>
              <div>
                <Label>Contrase{'\u00f1'}a *</Label>
                <Input type="password" value={newTrainer.password} onChange={(e) => setNewTrainer(p => ({...p, password: e.target.value}))} placeholder="Min 6 caracteres" className="bg-zinc-900/50 border-zinc-800" />
              </div>
              <div>
                <Label>Especialidad</Label>
                <Input value={newTrainer.specialty} onChange={(e) => setNewTrainer(p => ({...p, specialty: e.target.value}))} placeholder="Ej: Fuerza, Rehabilitaci\u00f3n, Senior..." className="bg-zinc-900/50 border-zinc-800" />
              </div>
              <div>
                <Label>Tel{'\u00e9'}fono</Label>
                <Input value={newTrainer.phone} onChange={(e) => setNewTrainer(p => ({...p, phone: e.target.value}))} placeholder="Opcional" className="bg-zinc-900/50 border-zinc-800" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
              <Button onClick={handleCreate} disabled={creating}>
                {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Crear Entrenador
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Trainer Detail Dialog */}
        <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
          <DialogContent className="bg-card border-border max-w-2xl">
            <DialogHeader>
              <DialogTitle>{showDetail?.name}</DialogTitle>
            </DialogHeader>
            {showDetail && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-muted-foreground">Email:</span> <span className="text-foreground ml-2">{showDetail.email}</span></div>
                  <div><span className="text-muted-foreground">Especialidad:</span> <span className="text-foreground ml-2">{showDetail.specialty || '-'}</span></div>
                  <div><span className="text-muted-foreground">Estado:</span> <Badge variant={showDetail.status === 'active' ? 'default' : 'destructive'} className="ml-2">{showDetail.status}</Badge></div>
                  <div><span className="text-muted-foreground">Desde:</span> <span className="text-foreground ml-2">{showDetail.created_at?.slice(0, 10)}</span></div>
                </div>

                {showDetail.clients?.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Clientes ({showDetail.clients.length})</h3>
                    <div className="space-y-1">
                      {showDetail.clients.map(c => (
                        <div key={c.id} className="flex items-center justify-between p-2 bg-surface rounded text-sm">
                          <span className="text-foreground">{c.name}</span>
                          <span className="text-muted-foreground">{c.email}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {showDetail.plans?.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Planes ({showDetail.plans.length})</h3>
                    <div className="space-y-1">
                      {showDetail.plans.map(p => (
                        <div key={p.id} className="flex items-center justify-between p-2 bg-surface rounded text-sm">
                          <span className="text-foreground">{p.name}</span>
                          <span className="text-muted-foreground">{p.weeks?.length || 0} semanas</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminTrainersPage;
